"use client";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import RegulatoryStatusBadge from "./RegulatoryStatusBadge";

type FooterLink = { label: string; href: string };
type FooterColumn = { title: string; links: FooterLink[] };

// Every link below points at a real, existing route in the app - no
// placeholder or invented pages. /about, /contact, and /careers are new,
// intentionally minimal static pages added so the "Company" column has
// somewhere real to go (see app/about, app/contact, app/careers).
const COLUMNS: FooterColumn[] = [
  {
    title: "Platform",
    links: [
      { label: "Wallet Search", href: "/search" },
      { label: "Live Activity Feed", href: "/transactions/live" },
      { label: "Charities", href: "/charities" },
      { label: "Fundraisers", href: "/fundraisers" },
      { label: "Phone Book", href: "/phone-book" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Methodology", href: "/methodology" },
      { label: "Data Sources", href: "/data-sources" },
      { label: "Trending", href: "/trending" },
      { label: "Discord Community", href: "/discord" },
    ],
  },
  {
    title: "Security",
    links: [
      { label: "Report a Scam URL", href: "/url-threat-scan" },
      { label: "Wallet Risk Scoring", href: "/wallet-analysis" },
      { label: "Appeal a Flag", href: "/appeal-process" },
      { label: "Identity Verification", href: "/login" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Reqrium", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Disclaimer & Regulatory Status", href: "/disclaimer" },
    ],
  },
];

function GithubMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 4 0c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

/* CONTRAST NOTE (2026-08-30): the four colours below are set inline, which
   beats every stylesheet rule, so they could not be corrected from
   styles/globals.css. Measured against the footer's own dark ground they were
   #5B7FFF 3.83:1, #94a3b8 2.35:1, #64748b 2.85:1 - all under the 4.5:1 AA
   floor for body text. The replacements keep the same hues and clear it. The
   footer stays dark in both themes by design, so these are theme-independent. */
export default function Footer() {
  const [year, setYear] = useState<number | null>(null);
  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  return (
    <footer
      className="page-section relative mt-14 w-full pt-14 pb-8"
      style={{ backgroundColor: "transparent" }}
    >
      {/* Brand-gradient accent line replaces the old flat white border for a
          more deliberate, on-brand top edge (matches the divider used in the
          Hero card instead of a generic hairline). */}
      <span
        className="bg-brand-gradient-divider absolute inset-x-0 top-0 block h-[2px] opacity-70"
        aria-hidden="true"
      />
      <div className="section-panel mx-auto max-w-7xl rounded-[32px] px-5 py-8 sm:px-8 sm:py-10">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <div className="flex items-center gap-2.5">
              {/* Same brand mark as the navbar - see the note in Navbar.tsx. */}
              <Image src="/svg/logo/reqrium-eye-mark.svg" alt="Reqrium Logo" width={32} height={32} priority />
              <span
                className="font-bold text-lg tracking-tight"
                style={{
                  background: "linear-gradient(90deg, #00F5FF 0%, #147CFF 40%, #7A35FF 75%, #E65CFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Reqrium
              </span>
            </div>
            <p className="mt-4 max-w-xs text-[13px] leading-relaxed" style={{ color: "#B6C2D6" }}>
              Real-time wallet intelligence, trust verification, and scam detection for Web3 - built so
              donors, partners, and compliance teams can verify before they engage.
            </p>
            <div className="mt-5 rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.04] px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100/80">Operator-grade trust stack</p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-300/85">Designed for investigation, donor confidence, and safer counterparties.</p>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Link
                href="/discord"
                aria-label="Discord"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.03] text-[#94a3b8] transition-colors hover:border-[#5B7FFF]/40 hover:text-[#5B7FFF]"
              >
                <Image src="/icons/discord.svg" alt="" width={16} height={16} />
              </Link>
              <a
                href="https://github.com/LegionofMany/411BlockPages"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.03] text-[#94a3b8] transition-colors hover:border-[#00D4FF]/40 hover:text-[#00D4FF]"
              >
                <GithubMark />
              </a>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title} className="min-w-0 col-span-1 lg:col-span-1">
              <h3
                className="text-[11px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: "#9FB6FF" }}
              >
                {col.title}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-[13px] transition-colors hover:text-[#00D4FF]"
                      style={{ color: "#D3DCEA" }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-6 sm:flex-row">
          <p className="text-xs" style={{ color: "#A3B0C4" }}>
            &copy; {year ?? ""} Reqrium. All rights reserved.
          </p>
          <RegulatoryStatusBadge compact />
          <p className="text-xs font-mono" style={{ color: "#A3B0C4" }}>
            Built for trust across 75+ chains.
          </p>
        </div>
      </div>
    </footer>
  );
}
